// src/pages/AdminDashboard.js
import React, { useState, useEffect } from "react";
import { auth, db, rtdb } from "../firebase";
import { signOut } from "firebase/auth";
import { ref, set } from "firebase/database";
import { LOGO_SRC } from "../images";
import {
  collection, addDoc, getDocs, deleteDoc, doc,
  updateDoc, query, orderBy, setDoc
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import AdminPuzzles from "./AdminPuzzles";
import TrainingArchive from "./TrainingArchive";
import Database from "./Database";
import "./AdminDashboard.css";

// Firebase Web API key (public by design — same value as your firebase config).
// Used for the REST sign-up call that creates accounts without logging the admin out.
const FIREBASE_API_KEY = "AIzaSyAROU-k2K2E3hhlWpmPdqDKz1JOqU5xYzI";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const isAdmin = userRole === "admin"; // coaches share this dashboard but can't manage accounts
  const [tab, setTab] = useState("classes"); // classes | students | coaches | puzzles
  const [classSearch, setClassSearch] = useState(""); // filter classes by title
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [showAddClass, setShowAddClass] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showAddCoach, setShowAddCoach] = useState(false);
  const [loading, setLoading] = useState(true);

  // New class form
  const [newClass, setNewClass] = useState({
    title: "", date: "", time: "", studentIds: []
  });

  // New student form
  const [newStudent, setNewStudent] = useState({ name: "", email: "", password: "" });
  const [studentMsg, setStudentMsg] = useState("");

  // New coach form
  const [newCoach, setNewCoach] = useState({ name: "", email: "", password: "" });
  const [coachMsg, setCoachMsg] = useState("");

  useEffect(() => {
    fetchClasses();
    fetchStudents();
    if (isAdmin) fetchCoaches();
  }, [isAdmin]);

  async function fetchClasses() {
    setLoading(true);
    const q = query(collection(db, "classes"), orderBy("datetime", "asc"));
    const snap = await getDocs(q);
    setClasses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  async function fetchStudents() {
    const snap = await getDocs(collection(db, "students"));
    setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  async function fetchCoaches() {
    const snap = await getDocs(collection(db, "coaches"));
    setCoaches(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  async function createClass() {
    if (!newClass.title || !newClass.date || !newClass.time) return;
    const datetime = new Date(`${newClass.date}T${newClass.time}`).toISOString();
    await addDoc(collection(db, "classes"), {
      title: newClass.title,
      datetime,
      studentIds: newClass.studentIds,
      status: "scheduled",
      createdAt: new Date().toISOString()
    });
    setNewClass({ title: "", date: "", time: "", studentIds: [] });
    setShowAddClass(false);
    fetchClasses();
  }

  async function deleteClass(id) {
    if (!window.confirm("Delete this class?")) return;
    await deleteDoc(doc(db, "classes", id));
    fetchClasses();
  }

  async function startClass(id) {
    await updateDoc(doc(db, "classes", id), { status: "live" });
    navigate(`/class/${id}`);
  }

  async function createStudent() {
    if (!newStudent.name || !newStudent.email || !newStudent.password) return;
    setStudentMsg("");
    try {
      const res = await fetch(
        `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${FIREBASE_API_KEY}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: newStudent.email,
            password: newStudent.password,
            returnSecureToken: false
          })
        }
      );
      const data = await res.json();
      if (data.error) throw new Error(data.error.message);
      const uid = data.localId;
      await setDoc(doc(db, "students", uid), {
        name: newStudent.name,
        email: newStudent.email,
        uid,
        createdAt: new Date().toISOString()
      });
      setStudentMsg(`✓ Student "${newStudent.name}" created successfully!`);
      setNewStudent({ name: "", email: "", password: "" });
      fetchStudents();
    } catch (err) {
      setStudentMsg(`Error: ${err.message}`);
    }
  }

  // Admin-only: create a coach. Mirrors createStudent, but ALSO writes the
  // coach's uid into the Realtime Database `teachers` list so the security
  // rules let them write the live board/control/status.
  async function createCoach() {
    if (!isAdmin) return;
    if (!newCoach.name || !newCoach.email || !newCoach.password) return;
    setCoachMsg("");
    try {
      const res = await fetch(
        `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${FIREBASE_API_KEY}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: newCoach.email,
            password: newCoach.password,
            returnSecureToken: false
          })
        }
      );
      const data = await res.json();
      if (data.error) throw new Error(data.error.message);
      const uid = data.localId;
      // 1. Firestore: login recognizes this account as a coach
      await setDoc(doc(db, "coaches", uid), {
        name: newCoach.name,
        email: newCoach.email,
        uid,
        createdAt: new Date().toISOString()
      });
      // 2. Realtime DB: lets the coach write the live board (per security rules)
      await set(ref(rtdb, `teachers/${uid}`), true);
      setCoachMsg(`✓ Coach "${newCoach.name}" created successfully!`);
      setNewCoach({ name: "", email: "", password: "" });
      fetchCoaches();
    } catch (err) {
      setCoachMsg(`Error: ${err.message}`);
    }
  }

  async function deleteCoach(id, name) {
    if (!isAdmin) return;
    if (!window.confirm(`Remove coach "${name}"? They will lose teaching access.`)) return;
    await deleteDoc(doc(db, "coaches", id));        // remove the coach role
    await set(ref(rtdb, `teachers/${id}`), null);   // revoke board-write access
    fetchCoaches();
  }

  async function deleteStudent(id, name) {
    if (!window.confirm(`Remove student "${name}"?`)) return;
    await deleteDoc(doc(db, "students", id));
    fetchStudents();
  }

  function toggleStudentInClass(studentId) {
    setNewClass(prev => ({
      ...prev,
      studentIds: prev.studentIds.includes(studentId)
        ? prev.studentIds.filter(id => id !== studentId)
        : [...prev.studentIds, studentId]
    }));
  }

  function getStatusLabel(cls) {
    if (cls.status === "live") return <span className="tag tag-live">● LIVE</span>;
    if (cls.status === "ended") return <span className="tag tag-done">Ended</span>;
    return <span className="tag tag-scheduled">Scheduled</span>;
  }

  function formatDatetime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      weekday: "short", day: "numeric", month: "short",
      year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  }

  function getStudentNames(ids) {
    return ids?.map(id => students.find(s => s.id === id)?.name || "?").join(", ") || "—";
  }

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="sidebar-logo"><img src={LOGO_SRC} alt="ChessingIt" style={{ height: 56, width: "auto", objectFit: "contain" }} /></div>
        <nav>
          <button className={`sidebar-btn ${tab === "classes" ? "active" : ""}`} onClick={() => setTab("classes")}>
            <span>📅</span> Classes
          </button>
          <button className={`sidebar-btn ${tab === "students" ? "active" : ""}`} onClick={() => setTab("students")}>
            <span>👤</span> Students
          </button>
          {isAdmin && (
            <button className={`sidebar-btn ${tab === "coaches" ? "active" : ""}`} onClick={() => setTab("coaches")}>
              <span>🎓</span> Coaches
            </button>
          )}
          <button className={`sidebar-btn ${tab === "puzzles" ? "active" : ""}`} onClick={() => setTab("puzzles")}>
            <span>🧩</span> Puzzles
          </button>
          <button className={`sidebar-btn ${tab === "training" ? "active" : ""}`} onClick={() => setTab("training")}>
            <span>🏋️</span> Training
          </button>
          <button className={`sidebar-btn ${tab === "database" ? "active" : ""}`} onClick={() => setTab("database")}>
            <span>🗄️</span> Database
          </button>
          <button className="sidebar-btn" onClick={() => navigate("/analysis")}>
            <span>🔍</span> Analysis
          </button>
        </nav>
        <button className="sidebar-btn sidebar-logout" onClick={() => signOut(auth)}>
          <span>↩</span> Logout
        </button>
      </aside>

      <main className="admin-main">
        {/* CLASSES TAB */}
        <div style={{ display: tab === "classes" ? "block" : "none" }}>
            <div className="page-header">
              <div>
                <h2>Classes</h2>
                <p className="page-sub">Manage and launch your chess lessons</p>
              </div>
              <button className="btn btn-primary" onClick={() => setShowAddClass(true)}>+ New Class</button>
            </div>

            <div className="class-search-wrap">
              <input
                className="class-search"
                placeholder="🔎 Search classes by name…"
                value={classSearch}
                onChange={e => setClassSearch(e.target.value)}
              />
              {classSearch && <button className="class-search-clear" onClick={() => setClassSearch("")}>×</button>}
            </div>

            {showAddClass && (
              <div className="modal-overlay" onClick={() => setShowAddClass(false)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <h3>Schedule New Class</h3>
                  <div className="form-grid">
                    <div>
                      <label>Class Title</label>
                      <input value={newClass.title} onChange={e => setNewClass({ ...newClass, title: e.target.value })} placeholder="e.g. Opening Principles" />
                    </div>
                    <div>
                      <label>Date</label>
                      <input type="date" value={newClass.date} onChange={e => setNewClass({ ...newClass, date: e.target.value })} />
                    </div>
                    <div>
                      <label>Time</label>
                      <input type="time" value={newClass.time} onChange={e => setNewClass({ ...newClass, time: e.target.value })} />
                    </div>
                    <div className="full-width">
                      <label>Assign Students</label>
                      <div className="student-checkboxes">
                        {students.map(s => (
                          <label key={s.id} className="checkbox-label">
                            <input type="checkbox"
                              checked={newClass.studentIds.includes(s.id)}
                              onChange={() => toggleStudentInClass(s.id)}
                            />
                            {s.name}
                          </label>
                        ))}
                        {students.length === 0 && <p style={{ color: "var(--text-muted)" }}>No students yet. Add students first.</p>}
                      </div>
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={() => setShowAddClass(false)}>Cancel</button>
                    <button className="btn btn-primary" onClick={createClass}>Schedule Class</button>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="loading-screen" style={{ minHeight: 200 }}><div className="spinner" /></div>
            ) : (
              <div className="classes-list">
                {classes.length === 0 && (
                  <div className="empty-state">
                    <div>📅</div>
                    <p>No classes scheduled yet</p>
                    <button className="btn btn-primary" onClick={() => setShowAddClass(true)}>Schedule your first class</button>
                  </div>
                )}
                {classes.length > 0 && classes.filter(cls => !classSearch.trim() || (cls.title || "").toLowerCase().includes(classSearch.trim().toLowerCase())).length === 0 && (
                  <div className="empty-state"><div>🔍</div><p>No classes match "{classSearch}"</p></div>
                )}
                {classes
                  .filter(cls => !classSearch.trim() || (cls.title || "").toLowerCase().includes(classSearch.trim().toLowerCase()))
                  .map(cls => (
                  <div key={cls.id} className="class-card">
                    <div className="class-card-left">
                      <div className="class-card-top">
                        <h3>{cls.title}</h3>
                        {getStatusLabel(cls)}
                      </div>
                      <p className="class-time">🕐 {formatDatetime(cls.datetime)}</p>
                      <p className="class-students">👤 {getStudentNames(cls.studentIds)}</p>
                    </div>
                    <div className="class-card-actions">
                      {cls.status === "live" && (
                        <button className="btn btn-danger" onClick={() => navigate(`/class/${cls.id}`)}>Rejoin Live</button>
                      )}
                      {cls.status === "scheduled" && (
                        <button className="btn btn-success" onClick={() => startClass(cls.id)}>▶ Start Class</button>
                      )}
                      {cls.status === "ended" && (
                        <button className="btn btn-secondary" disabled>Ended</button>
                      )}
                      <button className="btn btn-secondary" style={{ color: "var(--danger)" }} onClick={() => deleteClass(cls.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>

        {/* PUZZLES TAB */}
        <div style={{ display: tab === "puzzles" ? "block" : "none" }}>
          <AdminPuzzles students={students} />
        </div>

        {/* TRAINING ARCHIVE TAB */}
        <div style={{ display: tab === "training" ? "block" : "none" }}>
          <TrainingArchive students={students} />
        </div>

        {/* DATABASE TAB — mounted only when open so previews size correctly */}
        {tab === "database" && (
          <div>
            <Database />
          </div>
        )}

        {/* STUDENTS TAB */}
        <div style={{ display: tab === "students" ? "block" : "none" }}>
            <div className="page-header">
              <div>
                <h2>Students</h2>
                <p className="page-sub">Manage student accounts and access</p>
              </div>
              {isAdmin && (
                <button className="btn btn-primary" onClick={() => setShowAddStudent(true)}>+ Add Student</button>
              )}
            </div>

            {showAddStudent && (
              <div className="modal-overlay" onClick={() => setShowAddStudent(false)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <h3>Add New Student</h3>
                  <div className="form-grid">
                    <div>
                      <label>Full Name</label>
                      <input value={newStudent.name} onChange={e => setNewStudent({ ...newStudent, name: e.target.value })} placeholder="Arjun Kumar" />
                    </div>
                    <div>
                      <label>Email</label>
                      <input type="email" value={newStudent.email} onChange={e => setNewStudent({ ...newStudent, email: e.target.value })} placeholder="student@email.com" />
                    </div>
                    <div>
                      <label>Password</label>
                      <input type="password" value={newStudent.password} onChange={e => setNewStudent({ ...newStudent, password: e.target.value })} placeholder="Set a password for them" />
                    </div>
                  </div>
                  {studentMsg && (
                    <p style={{ color: studentMsg.startsWith("✓") ? "var(--success)" : "var(--danger)", marginTop: 8, fontSize: 13 }}>{studentMsg}</p>
                  )}
                  <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={() => { setShowAddStudent(false); setStudentMsg(""); }}>Close</button>
                    <button className="btn btn-primary" onClick={createStudent}>Create Account</button>
                  </div>
                </div>
              </div>
            )}

            <div className="students-list">
              {students.length === 0 && (
                <div className="empty-state">
                  <div>👤</div>
                  <p>No students yet</p>
                  {isAdmin && (
                    <button className="btn btn-primary" onClick={() => setShowAddStudent(true)}>Add your first student</button>
                  )}
                </div>
              )}
              {students.map(s => (
                <div key={s.id} className="student-card">
                  <div className="student-avatar">{s.name?.charAt(0).toUpperCase()}</div>
                  <div className="student-info">
                    <h4>{s.name}</h4>
                    <p>{s.email}</p>
                  </div>
                  {isAdmin && (
                    <button className="btn btn-secondary" style={{ color: "var(--danger)" }} onClick={() => deleteStudent(s.id, s.name)}>Remove</button>
                  )}
                </div>
              ))}
            </div>
        </div>

        {/* COACHES TAB (admin only) */}
        {isAdmin && (
          <div style={{ display: tab === "coaches" ? "block" : "none" }}>
            <div className="page-header">
              <div>
                <h2>Coaches</h2>
                <p className="page-sub">Coaches can run classes, give puzzles, and use the analysis board — but cannot add students</p>
              </div>
              <button className="btn btn-primary" onClick={() => setShowAddCoach(true)}>+ Add Coach</button>
            </div>

            {showAddCoach && (
              <div className="modal-overlay" onClick={() => setShowAddCoach(false)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <h3>Add New Coach</h3>
                  <div className="form-grid">
                    <div>
                      <label>Full Name</label>
                      <input value={newCoach.name} onChange={e => setNewCoach({ ...newCoach, name: e.target.value })} placeholder="Coach name" />
                    </div>
                    <div>
                      <label>Email</label>
                      <input type="email" value={newCoach.email} onChange={e => setNewCoach({ ...newCoach, email: e.target.value })} placeholder="coach@email.com" />
                    </div>
                    <div>
                      <label>Password</label>
                      <input type="password" value={newCoach.password} onChange={e => setNewCoach({ ...newCoach, password: e.target.value })} placeholder="Set a password for them" />
                    </div>
                  </div>
                  {coachMsg && (
                    <p style={{ color: coachMsg.startsWith("✓") ? "var(--success)" : "var(--danger)", marginTop: 8, fontSize: 13 }}>{coachMsg}</p>
                  )}
                  <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={() => { setShowAddCoach(false); setCoachMsg(""); }}>Close</button>
                    <button className="btn btn-primary" onClick={createCoach}>Create Account</button>
                  </div>
                </div>
              </div>
            )}

            <div className="students-list">
              {coaches.length === 0 && (
                <div className="empty-state">
                  <div>🎓</div>
                  <p>No coaches yet</p>
                  <button className="btn btn-primary" onClick={() => setShowAddCoach(true)}>Add your first coach</button>
                </div>
              )}
              {coaches.map(c => (
                <div key={c.id} className="student-card">
                  <div className="student-avatar">{c.name?.charAt(0).toUpperCase()}</div>
                  <div className="student-info">
                    <h4>{c.name}</h4>
                    <p>{c.email}</p>
                  </div>
                  <button className="btn btn-secondary" style={{ color: "var(--danger)" }} onClick={() => deleteCoach(c.id, c.name)}>Remove</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
